import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Save, Download, Edit3, Eye, Trash2, FileText,
  BookOpen, Calendar, Tag, User, ExternalLink, Clock, Upload, Loader2, Cloud
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const STATUS_COLORS = {
  published: 'bg-green-100 text-green-700',
  draft: 'bg-yellow-100 text-yellow-700',
  archived: 'bg-gray-100 text-gray-500',
};

const KBArticleDetailPage = () => {
  const { kbId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [uploadingSP, setUploadingSP] = useState(false);

  useEffect(() => {
    fetchArticle();
  }, [kbId]);

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const res = await api.getKBArticle(kbId);
      setArticle(res.data);
      setEditContent(res.data.content_markdown || '');
      setEditTitle(res.data.title || '');
      setEditStatus(res.data.status || 'published');
    } catch (err) {
      console.error('Failed to fetch KB article:', err);
      toast.error('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.updateKBArticle(kbId, {
        title: editTitle,
        content_markdown: editContent,
        status: editStatus,
      });
      setArticle(res.data);
      setEditing(false);
      toast.success('Article updated');
    } catch (err) {
      toast.error('Failed to save: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this KB article permanently?')) return;
    try {
      await api.deleteKBArticle(kbId);
      toast.success('Article deleted');
      navigate('/kb');
    } catch (err) {
      toast.error('Failed to delete article');
    }
  };

  const handleDownload = () => {
    window.open(api.getKBDownloadUrl(kbId), '_blank');
  };

  const handleUploadToSharePoint = async () => {
    setUploadingSP(true);
    try {
      const res = await api.uploadKBToSharePoint(kbId);
      if (res.data.success) {
        toast.success('Uploaded to SharePoint!');
        setArticle(res.data.article);
      }
    } catch (err) {
      toast.error('SharePoint upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingSP(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Article not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kb-article-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/kb')} className="gap-2">
            <ChevronLeft className="h-4 w-4" />
            Knowledge Base
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
                {article.doc_id}
              </span>
              <Badge className={`text-xs ${STATUS_COLORS[article.status] || 'bg-gray-100'}`}>
                {article.status}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button variant="outline" onClick={() => { setEditing(false); setEditContent(article.content_markdown); setEditTitle(article.title); }}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="gap-2 bg-amber-600 hover:bg-amber-700" data-testid="kb-save-btn">
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-600 hover:text-red-700" data-testid="kb-delete-btn">
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="gap-2" data-testid="kb-edit-btn">
                <Edit3 className="h-4 w-4" />
                Edit
              </Button>
              <Button size="sm" onClick={handleDownload} className="gap-2 bg-amber-600 hover:bg-amber-700" data-testid="kb-download-btn">
                <Download className="h-4 w-4" />
                Download DOCX
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-3">
          {editing ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Edit Article</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Title</label>
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    data-testid="kb-edit-title"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Status</label>
                  <Select value={editStatus} onValueChange={setEditStatus}>
                    <SelectTrigger data-testid="kb-edit-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Content (Markdown)</label>
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={30}
                    className="font-mono text-sm"
                    data-testid="kb-edit-content"
                  />
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              {/* Branded header */}
              <div className="px-6 py-4 border-b bg-gradient-to-r from-red-800 to-red-900 rounded-t-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <BookOpen className="h-5 w-5 text-white/80" />
                    <span className="text-white/90 text-sm font-medium tracking-wide">
                      Gamer Packaging | INTERNAL KNOWLEDGE BASE
                    </span>
                  </div>
                  <span className="text-white/60 text-xs font-mono">{article.doc_id}</span>
                </div>
              </div>

              {/* Metadata bar */}
              <div className="px-6 py-3 bg-slate-50 border-b flex items-center gap-6 text-xs text-slate-500">
                <span><strong>Audience:</strong> {article.audience}</span>
                <span><strong>Owner:</strong> {article.owner}</span>
                <span><strong>Updated:</strong> {formatDate(article.updated_at)}</span>
                <Badge className={`text-xs ${STATUS_COLORS[article.status]}`}>{article.status}</Badge>
              </div>

              {/* Article content */}
              <CardContent className="py-8 px-8">
                <article className="prose prose-slate max-w-none prose-headings:text-slate-800 prose-h1:text-2xl prose-h1:border-b prose-h1:pb-3 prose-h2:text-lg prose-h2:text-red-800 prose-h2:border-b prose-h2:border-red-100 prose-h2:pb-2 prose-h2:mt-8 prose-h3:text-base prose-table:text-sm prose-blockquote:border-l-amber-500 prose-blockquote:bg-amber-50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-blockquote:not-italic prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-red-700 prose-code:before:content-none prose-code:after:content-none prose-pre:bg-slate-900 prose-pre:text-green-300">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {article.content_markdown}
                  </ReactMarkdown>
                </article>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Article Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <span className="text-xs text-gray-500">Document ID</span>
                <p className="font-mono text-amber-700">{article.doc_id}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Category</span>
                <p>{article.category || 'General'}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Created</span>
                <p className="flex items-center gap-1">
                  <Calendar className="h-3 w-3 text-gray-400" />
                  {formatDate(article.created_at)}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Last Updated</span>
                <p className="flex items-center gap-1">
                  <Clock className="h-3 w-3 text-gray-400" />
                  {formatDate(article.updated_at)}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Author</span>
                <p className="flex items-center gap-1">
                  <User className="h-3 w-3 text-gray-400" />
                  {article.created_by || 'AI Generated'}
                </p>
              </div>
              {article.source_ticket_number && (
                <div>
                  <span className="text-xs text-gray-500">Source Ticket</span>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 h-auto text-sm text-blue-600"
                    onClick={() => navigate(`/tickets/managed/${article.source_ticket_id}`)}
                    data-testid="kb-source-ticket-link"
                  >
                    <Tag className="h-3 w-3 mr-1" />
                    Ticket #{article.source_ticket_number}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="py-4 space-y-3">
              <Button
                className="w-full gap-2 bg-amber-600 hover:bg-amber-700"
                onClick={handleDownload}
                data-testid="kb-sidebar-download"
              >
                <Download className="h-4 w-4" />
                Download DOCX
              </Button>

              {article.sharepoint_url ? (
                <div className="space-y-2">
                  <a
                    href={article.sharepoint_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-medium rounded-md border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                    data-testid="kb-open-sharepoint"
                  >
                    <Cloud className="h-4 w-4" />
                    Open in SharePoint
                  </a>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2 text-xs"
                    onClick={handleUploadToSharePoint}
                    disabled={uploadingSP}
                    data-testid="kb-reupload-sharepoint"
                  >
                    {uploadingSP ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                    {uploadingSP ? 'Uploading...' : 'Re-upload to SharePoint'}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full gap-2 text-blue-600 border-blue-300 hover:bg-blue-50"
                  onClick={handleUploadToSharePoint}
                  disabled={uploadingSP}
                  data-testid="kb-upload-sharepoint"
                >
                  {uploadingSP ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploadingSP ? 'Uploading...' : 'Upload to SharePoint'}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default KBArticleDetailPage;
